import React, { useState, useEffect } from 'react';
import { useForm, FormProvider } from 'react-hook-form';
import {
  Check,
  ChevronRight,
  ChevronLeft,
  Send,
  Loader2,
  AlertCircle,
  Star,
  Download,
} from 'lucide-react';
import ThemeToggle from './ThemeToggle';
import { CompanyInfoStep } from './CompanyInfoStep';
import { OwnersAndReferencesStep } from './OwnersAndReferencesStep';
import { ShipmentDetailsStep } from './ShipmentDetailsStep';
import { AgreementsStep } from './AgreementsStep';
import { toast } from '../utils/toast';
import { generateApplicationPdf } from '../utils/generateApplicationPdf';
import { AEL_LOGO_DATA_URI } from '../../../lib/aelLogo';
import type { PublicInviteData, AuditLogEntry } from '../types';

const LOGO_URL = AEL_LOGO_DATA_URI;

const STEPS = [
  { id: 'company', title: 'Company Info' },
  { id: 'owners', title: 'Owners & References' },
  { id: 'shipment', title: 'Shipment Details' },
  { id: 'agreements', title: 'Agreements & Signatures' },
];

const DEFAULT_VALUES = {
  legalCompanyName: '',
  federalId: '',
  typeOfBusiness: '',
  wholesaleLicense: '',
  legalStatus: '',
  yearEstablished: '',
  billingAddress: '',
  city: '',
  state: '',
  zipcode: '',
  mainPhone: '',
  generalContactMobile: '',
  website: '',
  generalContact: '',
  generalContactEmail: '',
  apContact: '',
  apContactEmail: '',
  ownProperty: '',
  propertyOwnedBy: '',
  otherBusinessName: '',
  previousBusinessName: '',
  officers: [
    { nameAndTitle: '', birthDate: '', cityOfResidence: '' },
    { nameAndTitle: '', birthDate: '', cityOfResidence: '' },
    { nameAndTitle: '', birthDate: '', cityOfResidence: '' },
  ],
  tradeReferences: [
    { supplierName: '', telephone: '', email: '' },
    { supplierName: '', telephone: '', email: '' },
    { supplierName: '', telephone: '', email: '' },
  ],
  bankReference: { nameAndBranch: '', address: '', contactPerson: '', telephone: '', email: '' },
  deliveryAddress: '',
  emergencyContactName: '',
  emergencyContactPhone: '',
  emergencyContactEmail: '',
  receivingHours: '',
  access247: '',
  entryInstructions: '',
  activeAlarm: '',
  needLockbox: '',
  truckAccess53: '',
  dockSealed: '',
  doorTallerThan8ft: '',
  unloadingEquipment: '',
  pathLevelAndWide: '',
  multipleCoolers: '',
  ltlProduct: '',
  ltlTemperature: '',
  freightOrigin: '',
  terminalOrPickup: '',
  pickupAddress: '',
  currentCarrier: '',
  ltlVolume: '',
  routineShipment: '',
  acceptanceSignature: '',
  acceptanceDate: '',
  acceptanceNameTitle: '',
  securitySignature: '',
  securityDate: '',
  securityNameTitle: '',
  communicationSignature: '',
  communicationDate: '',
  communicationNameTitle: '',
  guaranteeSignature: '',
  guaranteeDate: '',
  guaranteeNameTitle: '',
  certificateUrl: '',
};

function LandingPage({
  companyName,
  signerName,
  instructions,
  onBegin,
}: {
  companyName: string;
  signerName: string;
  instructions?: string;
  onBegin: () => void;
}) {
  return (
    <div className="min-h-screen bg-slate-50 dark:bg-slate-950 py-12 px-4 transition-colors duration-300">
      <div className="max-w-3xl mx-auto">
        <header className="flex items-center justify-between mb-10">
          <img src={LOGO_URL} alt="Armellini Express Lines, Inc." className="h-12 object-contain" />
          <ThemeToggle />
        </header>

        <div className="bg-white dark:bg-slate-900 rounded-[3rem] shadow-xl border border-slate-100 dark:border-slate-800 overflow-hidden transition-colors duration-300">
          <div className="bg-slate-900 dark:bg-slate-800 px-12 py-10 text-center">
            <div className="flex items-center justify-center gap-3 mb-3">
              <Star className="text-amber-400" size={18} fill="currentColor" />
              <span className="text-amber-400 text-xs font-bold uppercase tracking-widest">New Account Application</span>
              <Star className="text-amber-400" size={18} fill="currentColor" />
            </div>
            <h1 className="text-3xl font-bold text-white">Welcome to Armellini Logistics</h1>
          </div>

          <div className="p-12 space-y-8">
            {signerName && (
              <p className="text-slate-700 dark:text-slate-300 leading-relaxed">
                Dear <strong>{signerName}</strong>,
              </p>
            )}

            {instructions ? (
              <div
                className="text-slate-600 dark:text-slate-400 leading-relaxed prose prose-sm max-w-none"
                dangerouslySetInnerHTML={{ __html: instructions }}
              />
            ) : (
              <p className="text-slate-600 dark:text-slate-400 leading-relaxed">
                Thank you for your interest in doing business with{companyName ? ` ${companyName} and` : ''} Armellini Logistics. To process your credit application, please complete the form below.
              </p>
            )}

            <div className="bg-indigo-50 dark:bg-indigo-900/20 rounded-[2rem] p-8 border border-indigo-100 dark:border-indigo-800/30">
              <h2 className="text-xs font-bold text-indigo-700 dark:text-indigo-400 uppercase tracking-widest mb-6">What to Expect</h2>
              <div className="space-y-5">
                {[
                  { label: 'Completed Credit Application', note: 'All sections of this online form must be fully completed and signed.' },
                  { label: 'Credit References (Optional)', note: 'Only required if you wish to be considered for credit terms (Net 15, Net 30). Miami, FL flower suppliers are preferred.' },
                ].map((item, i) => (
                  <div key={i} className="flex items-start gap-4">
                    <div className="w-7 h-7 rounded-full bg-indigo-600 text-white flex items-center justify-center text-xs font-bold flex-shrink-0 mt-0.5">{i + 1}</div>
                    <div>
                      <p className="font-semibold text-slate-900 dark:text-slate-100 text-sm">{item.label}</p>
                      <p className="text-sm text-slate-500 dark:text-slate-400 mt-0.5">{item.note}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <div className="bg-slate-50 dark:bg-slate-800/50 rounded-[2rem] p-8 border border-slate-100 dark:border-slate-800">
              <p className="text-sm text-slate-600 dark:text-slate-400">Questions about this application? Please contact:</p>
              <div className="mt-3">
                <p className="font-semibold text-slate-900 dark:text-slate-100">Armellini Customer Center</p>
                <a href="mailto:ccenter@armellini.com" className="text-indigo-600 dark:text-indigo-400 text-sm hover:underline">
                  ccenter@armellini.com
                </a>
              </div>
            </div>

            <button
              onClick={onBegin}
              className="w-full flex items-center justify-center gap-3 bg-indigo-600 hover:bg-indigo-700 text-white px-10 py-5 rounded-2xl font-bold text-base transition-all shadow-lg shadow-indigo-200 dark:shadow-indigo-900/20 cursor-pointer"
            >
              Begin Credit Application <ChevronRight size={20} />
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

interface CreditApplicationFormProps {
  invite: PublicInviteData;
  onSubmitted?: () => void;
}

export default function CreditApplicationForm({ invite, onSubmitted }: CreditApplicationFormProps) {
  const [loading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [completed, setCompleted] = useState(invite.status === 'completed');
  const [error] = useState<string | null>(null);
  const [showLanding, setShowLanding] = useState(invite.status !== 'completed');
  const [currentStep, setCurrentStep] = useState(0);
  const [ip, setIp] = useState('Unknown');
  const [openedAt] = useState(new Date());
  const [pdfBlobUrl, setPdfBlobUrl] = useState<string | null>(null);

  const methods = useForm({
    defaultValues: {
      ...DEFAULT_VALUES,
      legalCompanyName: invite.companyName || '',
      generalContact: invite.signerName || '',
      generalContactEmail: invite.customerEmail || '',
    },
  });

  // Fetch client IP for audit log
  useEffect(() => {
    fetch('https://api.ipify.org?format=json')
      .then((r) => r.json())
      .then((d) => setIp(d.ip || 'Unknown'))
      .catch(() => {});
  }, []);

  const getFieldsForStep = (step: number): string[] => {
    switch (step) {
      case 0:
        return [
          'legalCompanyName',
          'federalId',
          'typeOfBusiness',
          'billingAddress',
          'city',
          'state',
          'zipcode',
          'mainPhone',
          'generalContact',
          'generalContactEmail',
          'apContact',
          'apContactEmail',
        ];
      case 1:
        return [];
      case 2:
        return [
          'deliveryAddress',
          'emergencyContactName',
          'emergencyContactPhone',
          'emergencyContactEmail',
        ];
      case 3:
        return ['acceptanceSignature', 'acceptanceDate', 'acceptanceNameTitle'];
      default:
        return [];
    }
  };

  const nextStep = async (e?: React.MouseEvent<HTMLButtonElement>) => {
    e?.preventDefault();
    const valid = await methods.trigger(getFieldsForStep(currentStep) as any);
    if (valid) {
      setCurrentStep((s) => Math.min(s + 1, STEPS.length - 1));
      window.scrollTo(0, 0);
    }
  };

  const prevStep = () => {
    setCurrentStep((s) => Math.max(s - 1, 0));
    window.scrollTo(0, 0);
  };

  const onSubmit = async (data: any) => {
    setSubmitting(true);

    try {
      const userAgent = navigator.userAgent;
      const nowStr = new Date().toISOString();

      const auditLog: AuditLogEntry[] = [
        {
          action: 'Invite Created',
          timestamp: invite.createdAt || nowStr,
          details: `Invitation sent to ${invite.customerEmail}`,
          ip: 'System',
          userAgent: 'System',
        },
        {
          action: 'Application Opened',
          timestamp: openedAt.toISOString(),
          details: 'Application link accessed by customer',
          ip,
          userAgent,
        },
        {
          action: 'Form Completed',
          timestamp: nowStr,
          details: 'Customer completed all steps of credit application',
          ip,
          userAgent,
        },
        {
          action: 'Application Submitted',
          timestamp: nowStr,
          details: `Final application submitted for ${data.legalCompanyName}`,
          ip,
          userAgent,
        },
      ];

      // Map to jsPDF generator format
      const mappedFormData = {
        general: {
          legalName: data.legalCompanyName || '',
          dba: data.otherBusinessName || '',
          address: data.billingAddress || '',
          city: data.city || '',
          state: data.state || '',
          zip: data.zipcode || '',
          phone: data.mainPhone || '',
          invoiceTo: data.generalContactEmail || '',
          buyerName: data.generalContact || '',
          buyerPhone: data.generalContactMobile || '',
          buyerEmail: data.generalContactEmail || '',
          apName: data.apContact || '',
          apEmail: data.apContactEmail || '',
          apPhone: data.mainPhone || '',
          fedTaxId: data.federalId || '',
          companyType: data.typeOfBusiness || data.legalStatus || '',
        },
        tradeReferences: (data.tradeReferences || []).map((t: any) => ({
          name: t.supplierName || '',
          phone: t.telephone || '',
          email: t.email || '',
        })),
        shipping: {
          carrier: data.currentCarrier || '',
          account: '',
          days: {},
        },
        agreements: {
          policySignature: data.acceptanceSignature || '',
          signDate: data.acceptanceDate || new Date().toISOString().split('T')[0],
        },
      };

      let pdfBase64: string | null = null;
      try {
        const pdfRes = await generateApplicationPdf(
          mappedFormData,
          invite.signerName || data.generalContact || data.legalCompanyName,
          undefined,
          {
            appId: invite.id,
            signerEmail: invite.customerEmail || data.generalContactEmail,
            ip,
            userAgent,
            timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone,
            events: auditLog,
          }
        );
        if (pdfRes?.base64) {
          pdfBase64 = pdfRes.base64;
          setPdfBlobUrl(pdfRes.blobUrl);
        }
      } catch (pdfErr) {
        console.error('PDF generation error:', pdfErr);
      }

      // Submit to CRM Backend API
      const response = await fetch(`/api/public/forms/${invite.id}/submit`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          customerName: data.legalCompanyName || invite.companyName,
          customerEmail: data.generalContactEmail || invite.customerEmail,
          formData: data,
          signedPdfBase64: pdfBase64 ? `data:application/pdf;base64,${pdfBase64}` : null,
          attachments: [],
        }),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error || 'Failed to submit application');
      }

      setCompleted(true);
      toast.success('Credit application submitted successfully!');
      if (onSubmitted) onSubmitted();
    } catch (err: any) {
      console.error('Submission error:', err);
      toast.error(err.message || 'Failed to submit application — please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  if (completed) {
    return (
      <div className="min-h-screen bg-slate-50 dark:bg-slate-950 flex items-center justify-center p-4">
        <div className="bg-white dark:bg-slate-900 p-10 rounded-[3rem] shadow-xl border border-slate-100 dark:border-slate-800 max-w-md w-full text-center space-y-5">
          <div className="flex justify-start">
            <img src={LOGO_URL} alt="Armellini Express Lines, Inc." className="h-9 object-contain" />
          </div>
          <div className="w-16 h-16 bg-emerald-100 dark:bg-emerald-900/30 text-emerald-600 rounded-full flex items-center justify-center mx-auto mb-4">
            <Check className="w-8 h-8" />
          </div>
          <h2 className="text-2xl font-bold text-slate-900 dark:text-slate-50">Application Submitted!</h2>
          <p className="text-slate-600 dark:text-slate-400 text-sm leading-relaxed">
            Thank you for submitting your credit application to Armellini Logistics. Your signed agreement is securely recorded and our team has been notified.
          </p>

          {pdfBlobUrl && (
            <div className="pt-2">
              <a
                href={pdfBlobUrl}
                download={`Credit_Application_${(invite?.companyName || 'Armellini').replace(/\s+/g, '_')}.pdf`}
                className="inline-flex items-center gap-2 bg-indigo-600 hover:bg-indigo-700 text-white px-6 py-3 rounded-xl font-semibold text-sm transition-all shadow-md"
              >
                <Download size={16} /> Download Signed PDF
              </a>
            </div>
          )}
        </div>
      </div>
    );
  }

  if (showLanding) {
    return (
      <LandingPage
        companyName={invite?.companyName || ''}
        signerName={invite?.signerName || ''}
        instructions={invite?.instructions || undefined}
        onBegin={() => setShowLanding(false)}
      />
    );
  }

  return (
    <div className="min-h-screen bg-slate-50 dark:bg-slate-950 py-12 px-4 transition-colors duration-300">
      <div className="max-w-4xl mx-auto">
        <header className="mb-10">
          <div className="flex items-center justify-between mb-6">
            <img src={LOGO_URL} alt="Armellini Express Lines, Inc." className="h-11 object-contain" />
            <ThemeToggle />
          </div>
          <div className="text-center">
            <h1 className="text-2xl sm:text-3xl font-bold text-slate-900 dark:text-slate-50">
              Credit Application & Agreement
            </h1>
            <p className="text-sm text-slate-500 dark:text-slate-400 mt-1">
              {invite.companyName}
            </p>
          </div>
        </header>

        {/* Progress Bar */}
        <div className="flex justify-between items-center mb-10 bg-white dark:bg-slate-900 p-4 rounded-3xl shadow-sm border border-slate-100 dark:border-slate-800 overflow-x-auto no-scrollbar">
          {STEPS.map((s, idx) => (
            <div key={s.id} className="flex items-center gap-2 shrink-0">
              <div
                className={`w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold transition-all ${
                  currentStep === idx
                    ? 'bg-indigo-600 text-white'
                    : currentStep > idx
                    ? 'bg-emerald-600 text-white'
                    : 'bg-slate-100 dark:bg-slate-800 text-slate-400'
                }`}
              >
                {currentStep > idx ? <Check size={14} /> : idx + 1}
              </div>
              <span
                className={`text-xs font-semibold ${
                  currentStep === idx
                    ? 'text-slate-900 dark:text-slate-100'
                    : 'text-slate-400 dark:text-slate-500'
                }`}
              >
                {s.title}
              </span>
              {idx < STEPS.length - 1 && <div className="w-8 h-0.5 bg-slate-100 dark:bg-slate-800 mx-2" />}
            </div>
          ))}
        </div>

        {/* Form Body */}
        <FormProvider {...methods}>
          <form onSubmit={methods.handleSubmit(onSubmit)} className="space-y-8">
            <div className="bg-white dark:bg-slate-900 rounded-[2.5rem] p-8 sm:p-12 shadow-sm border border-slate-100 dark:border-slate-800">
              {currentStep === 0 && <CompanyInfoStep />}
              {currentStep === 1 && <OwnersAndReferencesStep />}
              {currentStep === 2 && <ShipmentDetailsStep />}
              {currentStep === 3 && <AgreementsStep />}

              {/* Navigation controls */}
              <div className="flex items-center justify-between pt-8 mt-8 border-t border-slate-100 dark:border-slate-800">
                {currentStep > 0 ? (
                  <button
                    type="button"
                    onClick={prevStep}
                    disabled={submitting}
                    className="inline-flex items-center gap-2 px-6 py-3 rounded-2xl text-sm font-semibold text-slate-600 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors cursor-pointer"
                  >
                    <ChevronLeft size={16} /> Previous
                  </button>
                ) : (
                  <div />
                )}

                {currentStep < STEPS.length - 1 ? (
                  <button
                    key="next-step-btn"
                    type="button"
                    onClick={nextStep}
                    className="inline-flex items-center gap-2 bg-indigo-600 hover:bg-indigo-700 text-white px-8 py-3.5 rounded-2xl font-bold text-sm shadow-md transition-all cursor-pointer"
                  >
                    Next Step <ChevronRight size={16} />
                  </button>
                ) : (
                  <button
                    key="submit-app-btn"
                    type="submit"
                    disabled={submitting}
                    className="inline-flex items-center gap-2 bg-slate-900 dark:bg-indigo-600 hover:bg-slate-800 dark:hover:bg-indigo-700 text-white px-9 py-4 rounded-2xl font-bold text-sm shadow-lg transition-all cursor-pointer disabled:opacity-50"
                  >
                    {submitting ? (
                      <>
                        <Loader2 size={16} className="animate-spin" /> Submitting Application...
                      </>
                    ) : (
                      <>
                        <Send size={16} /> Submit Credit Application
                      </>
                    )}
                  </button>
                )}
              </div>
            </div>
          </form>
        </FormProvider>
      </div>
    </div>
  );
}
